import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Shield, ExternalLink, Globe2, Heart, HandHelping, Calendar, Newspaper } from "lucide-react";
import MessageBoard from "@/components/MessageBoard";

const TIER_LABEL = { bronze: "Bronze", silver: "Silver", gold: "Gold", platinum: "Platinum", verified: "Verified" };

function OrgLogo({ org, size = 112 }) {
  const [failed, setFailed] = useState(false);
  const src = org.logo_url || org.image_url;
  const monogram = (org.acronym || org.name || "?").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "?";
  const style = { width: size, height: size };
  if (!src || failed) {
    return (
      <div className="rounded-2xl grid place-items-center shrink-0 font-mono font-bold" style={{ ...style, background: "var(--wofy-surface)", color: "var(--wofy-primary)", fontSize: size / 3.2 }}>
        {monogram}
      </div>
    );
  }
  return (
    <div className="rounded-2xl overflow-hidden shrink-0 border grid place-items-center" style={{ ...style, borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
      <img src={src} alt={org.name} className="max-w-full max-h-full object-contain p-2" onError={() => setFailed(true)} />
    </div>
  );
}

// Long-form AI editorial (~1500 words) rendered as an editorial article. If the
// story hasn't been generated yet, polls the org endpoint until it appears (it
// is generated lazily on first visit by the backend).
function OrgStorySection({ org: initialOrg, orgKey }) {
  const [org, setOrg] = useState(initialOrg);
  const [pending, setPending] = useState(!initialOrg?.ai_story && initialOrg?.is_curated);

  useEffect(() => {
    setOrg(initialOrg);
    setPending(!initialOrg?.ai_story && !!initialOrg?.is_curated);
  }, [initialOrg]);

  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await http.get(`/organizations/${orgKey}`);
        if (cancelled) return;
        if (data?.organization?.ai_story) {
          setOrg(data.organization);
          setPending(false);
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < 40 && !cancelled) setTimeout(tick, 5000);
      else if (!cancelled) setPending(false); // give up after ~3 min
    };
    const t = setTimeout(tick, 4000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pending, orgKey]);

  const story = org?.ai_story;
  if (!story) {
    if (pending) {
      return (
        <section className="mt-2" data-testid="org-story-pending">
          <div className="wofy-card">
            <div className="overline flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--wofy-primary)" }} /> EDITORIAL PROFILE</div>
            <div className="font-display text-xl mt-2">Preparing an in-depth profile…</div>
            <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
              Our AI editor is drafting a long-form editorial about {org?.acronym || org?.name}. This usually takes 20–40 seconds on first visit — refresh in a minute or leave this tab open.
            </p>
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <section data-testid="org-story" className="mt-2">
      <div className="overline">EDITORIAL PROFILE · AI-ASSISTED</div>
      <h2 className="font-display text-2xl mt-2">About {org.acronym || org.name}</h2>

      {story.lead && (
        <p className="mt-5 text-lg leading-relaxed first-letter:font-display first-letter:text-5xl first-letter:mr-2 first-letter:float-left first-letter:leading-none" style={{ color: "var(--wofy-text)" }}>
          {story.lead}
        </p>
      )}

      {story.quote_pull && (
        <blockquote
          className="my-8 border-l-4 pl-5 py-1 font-display italic text-xl leading-snug"
          style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-text)" }}
        >
          “{story.quote_pull}”
        </blockquote>
      )}

      <div className="space-y-8 mt-6">
        {story.sections?.map((s, idx) => (
          <div key={idx}>
            <h3 className="font-display text-xl mb-2">{s.heading}</h3>
            <div className="space-y-3 text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
              {s.paragraphs?.map((p, i) => (<p key={i}>{p}</p>))}
            </div>
          </div>
        ))}
      </div>

      {story.how_to_help?.length > 0 && (
        <div className="mt-10 wofy-card">
          <div className="overline">HOW YOU CAN HELP</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            {story.how_to_help.map((h, i) => (
              <div key={i} className="text-sm flex items-start gap-2">
                <span className="text-[color:var(--wofy-primary)] mt-0.5">›</span>
                <span>{h}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-6 text-[11px] italic" style={{ color: "var(--wofy-text-soft)" }}>
        Editorial profile generated with AI assistance ({story.word_count || 0} words) based on publicly available information about {org.name}. Facts should be verified against the organization's official channels.
      </p>
    </section>
  );
}

export default function OrganizationDetail() {
  const { id: key } = useParams();
  const [data, setData] = useState(null);
  const [news, setNews] = useState({ mentions: [], matched_terms: [] });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setData(null);
    setNews({ mentions: [], matched_terms: [] });
    http.get(`/organizations/${key}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
    http.get(`/organizations/${key}/news`).then((r) => setNews(r.data)).catch(() => {});
  }, [key]);

  if (notFound) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="overline">ORGANIZATION</div>
        <h1 className="font-display text-3xl mt-3">We couldn't find that organization.</h1>
        <Link to="/organizations" className="wofy-btn-primary mt-6 inline-flex">Back to directory</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">Loading…</div>;

  const { organization: o, opportunities } = data;
  const donateUrl = o.donation_url || o.website_url;
  const volunteerUrl = o.volunteer_url || o.website_url;

  return (
    <div data-testid="org-detail" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14">
      {/* Breadcrumbs */}
      <div className="text-[11px] font-mono uppercase tracking-wider mb-6 opacity-60">
        <Link to="/organizations" className="hover:opacity-100">Organizations</Link>
        <span className="mx-2">/</span>
        <span>{o.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-6 md:items-center">
        <OrgLogo org={o} size={112} />
        <div className="flex-1 min-w-0">
          <div className="overline flex flex-wrap items-center gap-2">
            {o.verification_tier && <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3" style={{ color: "var(--wofy-primary)" }} /> {TIER_LABEL[o.verification_tier] || o.verification_tier}</span>}
            {o.year_founded && <span>· FOUNDED {o.year_founded}</span>}
            {o.reach && <span>· {o.reach.toUpperCase()}</span>}
          </div>
          <h1 className="font-display text-3xl md:text-5xl mt-2 leading-tight">{o.name}</h1>
          {o.acronym && o.acronym !== o.name && (
            <div className="text-sm font-mono uppercase tracking-wider mt-1 opacity-60">{o.acronym}{o.alt_names?.length ? ` · also known as ${o.alt_names.join(", ")}` : ""}</div>
          )}
          <p className="mt-4 text-base leading-relaxed max-w-3xl" style={{ color: "var(--wofy-text-soft)" }}>{o.tagline || o.mission}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 mt-12">
        {/* Main column */}
        <div className="lg:col-span-8">
          {/* Causes */}
          {o.causes?.length > 0 && (
            <section data-testid="org-causes">
              <div className="overline">CAUSES</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {o.causes.map((c) => (
                  <Link
                    key={c}
                    to={`/organizations?cause=${encodeURIComponent(c)}`}
                    className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border hover:bg-[color:var(--wofy-surface)] transition-colors"
                    style={{ borderColor: "var(--wofy-border)" }}
                  >
                    {c}
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Long-form AI editorial */}
          <OrgStorySection org={o} orgKey={key} />

          {/* News mentions */}
          <section className="mt-12" data-testid="org-news">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl flex items-center gap-2"><Newspaper className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} /> In the news</h2>
              <Link to="/things-to-know" className="text-xs font-mono uppercase tracking-wider opacity-60 hover:opacity-100">All briefings →</Link>
            </div>
            {news.mentions?.length > 0 ? (
              <div className="mt-5 space-y-4">
                {news.mentions.map((m) => (
                  <Link
                    key={m.id}
                    to={`/things-to-know/${m.id}`}
                    data-testid={`org-news-${m.id}`}
                    className="wofy-card block hover:border-[color:var(--wofy-primary)] transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      {m.image_url && (
                        <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 hidden sm:block">
                          <img src={m.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-mono uppercase tracking-wider opacity-60">
                          {m.source_name || m.source}
                          {m.category ? ` · ${m.category}` : ""}
                          {m.published_at ? ` · ${new Date(m.published_at).toLocaleDateString()}` : ""}
                        </div>
                        <div className="font-display text-lg mt-1.5 line-clamp-2">{m.title}</div>
                        {(m.ai_summary || m.summary) && (
                          <p className="text-sm mt-2 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{m.ai_summary || m.summary}</p>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
                No recent World-of-Joy news briefings mention {o.acronym || o.name} yet. Check back after our next hourly refresh.
              </p>
            )}
          </section>

          {/* Opportunities */}
          {opportunities?.length > 0 && (
            <section className="mt-14" data-testid="org-opportunities">
              <h2 className="font-display text-2xl mb-6">Open opportunities</h2>
              <div className="space-y-4">
                {opportunities.map((op) => (
                  <div key={op.id} className="wofy-card flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="overline">{op.date} · {op.hours}H · {(op.city || "").toUpperCase()}</div>
                      <div className="font-display text-xl mt-2">{op.title}</div>
                      <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{op.description}</p>
                    </div>
                    <Link to="/opportunities" className="wofy-btn-ghost whitespace-nowrap">View</Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Community message board */}
          <div className="mt-14">
            <MessageBoard
              listPath={`/organizations/${key}/messages`}
              postPath={`/organizations/${key}/messages`}
              deletePath={`/organizations/messages/{id}`}
              title={`Community messages for ${o.acronym || o.name}`}
              subtitle={`Share how ${o.acronym || o.name}'s work has touched your life, ask questions, or offer help.`}
              testid="org-message-board"
            />
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-6">
          {/* Primary action */}
          {donateUrl && (
            <div className="wofy-card" data-testid="org-donate-card">
              <div className="overline">SUPPORT DIRECTLY</div>
              <div className="font-display text-xl mt-2">Give to {o.acronym || o.name}</div>
              <p className="text-xs mt-2" style={{ color: "var(--wofy-text-soft)" }}>Donations go through the organization's official channel.</p>
              <a href={donateUrl} target="_blank" rel="noopener noreferrer" data-testid="org-donate-btn" className="wofy-btn-primary mt-4 w-full justify-center inline-flex items-center gap-2">
                <Heart className="w-4 h-4" /> Donate on official site <ExternalLink className="w-3.5 h-3.5" />
              </a>
              {volunteerUrl && volunteerUrl !== donateUrl && (
                <a href={volunteerUrl} target="_blank" rel="noopener noreferrer" data-testid="org-volunteer-btn" className="wofy-btn-ghost mt-2 w-full justify-center inline-flex items-center gap-2">
                  <HandHelping className="w-4 h-4" /> Volunteer <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          )}

          {/* Facts */}
          <div className="wofy-card">
            <div className="overline">FACT SHEET</div>
            <dl className="mt-3 space-y-2.5 text-sm">
              {o.year_founded && (
                <div className="flex justify-between gap-3"><dt className="opacity-60 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Founded</dt><dd className="font-mono">{o.year_founded}</dd></div>
              )}
              {(o.hq_city || o.hq_country) && (
                <div className="flex justify-between gap-3"><dt className="opacity-60 flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" /> HQ</dt><dd className="text-right">{[o.hq_city, o.hq_country].filter(Boolean).join(", ")}</dd></div>
              )}
              {o.reach && (
                <div className="flex justify-between gap-3"><dt className="opacity-60">Reach</dt><dd>{o.reach}</dd></div>
              )}
              {o.website_url && (
                <div className="flex justify-between gap-3"><dt className="opacity-60">Website</dt><dd><a href={o.website_url} target="_blank" rel="noopener noreferrer" className="text-[color:var(--wofy-primary)] hover:underline inline-flex items-center gap-1">Official <ExternalLink className="w-3 h-3" /></a></dd></div>
              )}
              {o.domain && (
                <div className="flex justify-between gap-3"><dt className="opacity-60">Domain</dt><dd className="font-mono opacity-70">{o.domain}</dd></div>
              )}
            </dl>
          </div>

          {/* Disclaimer */}
          <div className="text-[11px] leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            Information about {o.acronym || o.name} is compiled from public sources. Social Pulse is not affiliated with this organization; visit the official site for the latest details.
          </div>
        </aside>
      </div>
    </div>
  );
}
